import { useEffect, useState } from 'react';

import { de } from '../i18n/de';
import { BAND_HEIGHTS } from '../core/profile/cssVars';
import { useComposerStore } from '../core/composer/useComposer';
import { getSettings } from '../core/profile/ProfileStore';
import { quickFireRepo } from '../data/repositories';
import { seedSettled } from '../data/seed';
import { speechService } from '../core/speech/SpeechService';
import { useUiStore } from '../app/uiStore';
import { ActivateButton } from './ActivateButton';
import { ContentBand } from './ContentBand';
import { offerKeep } from './KeepOffer';
import { ProtectiveGap } from './ProtectiveGap';
import type { QuickFire } from '../core/types';

/**
 * The bar that never moves, on every surface, in the same place.
 *
 * Row 1 — the six quick fires. They carry more conversational weight per hit than any sentence,
 * so they are fixed in set and order, exempt from learning, and not editable by the person
 * (§4.1). Burying them in a category or letting usage reorder them would be a serious error.
 *
 * Row 2 — `Wort löschen · Löschen ‖ Sätze · Notfall · STOP · Sprechen`. Six targets because
 * nothing in the design doc ever said how the person reaches another surface and the vertical
 * budget has no band for navigation, so navigation lives here instead of costing a band. Notfall
 * is here because 1.10 requires it in one action from everywhere.
 *
 * **The separation of the destructive pair is a safety feature, not decoration.** A mis-hit that
 * deletes the sentence instead of saying "ja" is exactly the failure this person cannot afford, so
 * there is a `ProtectiveGap` — real space and a visible rule — beside `Wort löschen` and `Löschen`.
 *
 * They sit at the **far left**, and `Sprechen` at the far right at double width. The two actions that
 * must never be confused are now four targets apart rather than adjacent across one gap, and the one
 * that ends every sentence is the biggest thing in the bar. It costs the positions of `Notfall`, `STOP`
 * and the deletes, which is a debt paid once, before the device is in daily use and while nobody's
 * motor memory holds the old arrangement (`docs/layout-adaptation.md`). Scanning reads its order from
 * measured geometry, so `Wort löschen` is now the first item the cycle reaches after the hoisted STOP
 * row — nothing to change, something to watch on the device.
 *
 * There is **no ALARM button**: FR7 was withdrawn. A call-bell needs the iPad awake, charged, in
 * reach, unlocked, foregrounded and audible, so it belongs to the physical button in the kit.
 */

const ROW_HEIGHT = BAND_HEIGHTS.bottomRow;

export function PersistentBar(): React.JSX.Element {
  const [quickFires, setQuickFires] = useState<QuickFire[]>([]);
  const surface = useUiStore((s) => s.surface);
  const goTo = useUiStore((s) => s.goTo);
  const composer = useComposerStore();
  const hasText = composer.state.text.trim() !== '';

  // A restore replaces every quick fire, so the bar has to read them again — otherwise it would keep showing
  // the previous device's until a navigation remounted it.
  const dataVersion = useUiStore((s) => s.dataVersion);

  useEffect(() => {
    let live = true;
    const load = (): void => {
      void quickFireRepo.all().then((rows) => {
        if (live) setQuickFires(rows);
      });
    };

    load();
    // A first launch races the seed, and this bar is the only surface mounted while that race is still
    // running — so the read above can resolve against an empty database and leave the person with no
    // `Ja`, no `Nein` and no `Stopp` until a navigation happens to remount the bar. Re-reading when
    // seeding settles closes that window. It stays a re-read rather than a wait, because a device that
    // is already seeded must not have its bar gated on a promise that will never resolve again.
    void seedSettled.then(load);

    return () => {
      live = false;
    };
  }, [dataVersion]);

  const speakQuickFire = (quickFire: QuickFire): void => {
    // isPhrase so the result caches: these are the utterances that must work offline, and they
    // become instant once rendered.
    void speechService.speak({ text: quickFire.text, source: 'quickFire', isPhrase: true });
  };

  const speakComposer = (): void => {
    const text = composer.text();
    if (text === '') return;
    void speechService.speak({ text, source: 'keyboard' }).then((result) => {
      // Committed only once speech was dispatched: history should record what was said, not what
      // was typed and abandoned.
      if (!result) return;
      // Whether the sentence survives being spoken is the caregiver's setting. History is written
      // either way; only the buffer differs.
      void composer.commit('keyboard', { keepText: getSettings().keepTextAfterSpeak });

      // The sentence the person just composed is the best possible source of a tile, so offer to keep it
      // (`AUT-05`). After `commit`, because the `keepTextAfterSpeak` path appends a space and the offer
      // records the buffer as it now is. Nothing is awaited and nothing can refuse: this is beside the
      // speech path, never on it.
      offerKeep(text);

      // 3.9, off by default. Scoped to the composed-sentence path deliberately: showing a typed sentence to a
      // partner is useful, and jumping to a full-screen text after tapping "Ja" would be absurd.
      if (getSettings().autoPartnerAfterSpeak) goTo('partner');
    });
  };

  return (
    <div style={{ flex: '0 0 auto' }}>
      <ContentBand>
        {/* Row 1 — quick fires */}
        <div
          data-testid="quick-fires"
          style={{ display: 'flex', gap: 'var(--key-gap)', height: ROW_HEIGHT }}
        >
          {quickFires.map((quickFire) => (
            <BarButton
              key={quickFire.id}
              id={`quickfire:${quickFire.id}`}
              label={quickFire.text}
              onPress={() => speakQuickFire(quickFire)}
            />
          ))}
        </div>

        {/* The divider. Visible rule plus real space, so the destructive pair is separated by
            more than a colour change. */}
        <div
          aria-hidden="true"
          style={{
            height: BAND_HEIGHTS.divider,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <span style={{ width: '100%', height: 2, background: 'var(--divider)' }} />
        </div>

        {/* Row 2 — the destructive pair, then the gap, then navigation, STOP and speak */}
        <div
          data-testid="action-row"
          style={{ display: 'flex', gap: 'var(--key-gap)', height: ROW_HEIGHT }}
        >
          <BarButton
            id="action:deleteWord"
            label={de.deleteWord}
            onPress={() => composer.deleteWord()}
            disabled={!hasText}
            variant="destructive"
          />
          {/* Delete *character*, per the vocabulary in SUR-18. There is deliberately no clear-all
              **button**, and none anywhere near this pair: a single tap that erases a sentence somebody
              spent a minute typing is precisely the failure the gap beside this exists to prevent.
              Clearing does now exist (`SUR-26`) — as a two-second still hold on the output bar, which is
              the far end of the screen from here and is undoable from the band. That is `SUR-04`'s
              argument satisfied by distance rather than waived, which is why nothing in this row does it. */}
          <BarButton
            id="action:deleteChar"
            label={de.deleteChar}
            onPress={() => composer.deleteChar()}
            disabled={!hasText}
            variant="destructive"
          />
          <ProtectiveGap />
          <BarButton
            id="nav:saetze"
            label={surface === 'saetze' ? de.surfaceSprechen : de.surfaceSaetze}
            onPress={() => goTo(surface === 'saetze' ? 'sprechen' : 'saetze')}
          />
          <BarButton
            id="nav:notfall"
            label={de.surfaceNotfall}
            onPress={() => goTo('notfall')}
            /* Colour *and* shape: never encode state or importance in colour alone (§5.2). */
            variant="notfall"
          />
          <BarButton id="action:stop" label={de.stop} onPress={() => speechService.stop()} variant="stop" />
          {/* Double width. It is the action every typed sentence ends with, and it was the same size as
              the button that opens a phrase list. */}
          <BarButton
            id="action:speak"
            label={de.speak}
            onPress={speakComposer}
            disabled={!hasText}
            variant="primary"
            grow={2}
          />
        </div>
      </ContentBand>
    </div>
  );
}

type Variant = 'default' | 'primary' | 'destructive' | 'notfall' | 'stop';

function variantStyle(variant: Variant): React.CSSProperties {
  switch (variant) {
    case 'primary':
      return {
        background: 'var(--accent)',
        color: 'var(--accent-fg)',
        borderColor: 'var(--accent)',
        fontWeight: 700,
      };
    case 'destructive':
      return {
        background: 'var(--key-bg)',
        color: 'var(--danger)',
        borderColor: 'var(--danger)',
        borderStyle: 'dashed',
      };
    case 'notfall':
      return {
        background: 'var(--danger)',
        color: 'var(--danger-fg)',
        borderColor: 'var(--fg)',
        fontWeight: 700,
        // The shape difference: Notfall is the only cut-cornered target in the app, so it is
        // identifiable without relying on colour vision or on reading the label.
        borderRadius: 0,
        clipPath: 'polygon(12% 0, 100% 0, 100% 88%, 88% 100%, 0 100%, 0 12%)',
      };
    case 'stop':
      return {
        background: 'var(--fg)',
        color: 'var(--bg)',
        borderColor: 'var(--fg)',
        fontWeight: 700,
      };
    default:
      return { background: 'var(--key-bg)', color: 'var(--key-fg)', borderColor: 'var(--key-border)' };
  }
}

function BarButton({
  id,
  label,
  onPress,
  disabled = false,
  variant = 'default',
  grow = 1,
}: {
  id: string;
  label: string;
  onPress: () => void;
  disabled?: boolean;
  variant?: Variant;
  /** Share of the row's width, in units of one ordinary button. Only `Sprechen` asks for more. */
  grow?: number;
}): React.JSX.Element {
  return (
    <ActivateButton
      id={id}
      onActivate={onPress}
      disabled={disabled}
      style={{
        flex: `${grow} 1 0`,
        minWidth: 0,
        height: '100%',
        minHeight: 0,
        // 16 mm tall but much wider than a key, so the touch area is comparable (plan §0.1).
        fontSize: 'var(--font-button)',
        border: '3px solid',
        borderRadius: 'var(--radius)',
        ...variantStyle(variant),
      }}
    >
      {label}
    </ActivateButton>
  );
}
